/**
 * Theme configuration system
 * Provides configurable themes for the application
 */

import { getLocalStorage } from './browserUtils';

export const ThemePresets = {
  DARK: 'dark',
  LIGHT: 'light',
  CS_BLUE: 'cs-blue',
  R6_ORANGE: 'r6-orange',
  AUTO: 'auto', // Auto based on site type
  SYSTEM: 'system', // Auto based on system preference
};

/**
 * Detect system color scheme preference
 * @returns {boolean} true if system prefers dark mode
 */
export const prefersDarkMode = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * Get theme colors based on theme preset
 * @param {string} themeName - Theme preset name
 * @returns {Object} Theme color configuration
 */
export const getThemeColors = (themeName = ThemePresets.DARK) => {
  const storedTheme = getLocalStorage('theme', themeName);
  
  let activeTheme = storedTheme;
  if (activeTheme !== ThemePresets.LIGHT && activeTheme !== ThemePresets.DARK) {
    activeTheme = ThemePresets.DARK;
  }

  const themes = {
    [ThemePresets.DARK]: {
      primary: '#1e293b',
      secondary: '#334155',
      accent: '#3b82f6',
      controlAccent: '#2563eb',
      background: '#0f172a',
      surface: '#1e293b',
      text: '#f1f5f9',
      textSecondary: '#cbd5e1',
      border: '#334155',
      hover: '#475569',
      navbar: 'bg-slate-900',
      navbarText: 'text-slate-100',
      tabs: 'bg-slate-800',
      tabsText: 'text-slate-100',
      tabsIndicator: 'bg-blue-600',
    },
    [ThemePresets.LIGHT]: {
      primary: '#f1f5f9',
      secondary: '#e2e8f0',
      accent: '#2563eb',
      controlAccent: '#2563eb',
      background: '#f8fafc',
      surface: '#ffffff',
      text: '#0f172a',
      textSecondary: '#334155',
      border: '#7c8b9f',
      hover: '#e2e8f0',
      navbar: 'bg-white',
      navbarText: 'text-gray-900',
      tabs: 'bg-gray-100',
      tabsText: 'text-gray-900',
      tabsIndicator: 'bg-blue-600',
    },
    [ThemePresets.CS_BLUE]: {
      primary: '#1e40af',
      secondary: '#1e3a8a',
      accent: '#3b82f6',
      background: '#0f172a',
      surface: '#1e293b',
      text: '#e0e7ff',
      textSecondary: '#c7d2fe',
      border: '#334155',
      hover: '#2563eb',
      navbar: 'bg-blue-900',
      navbarText: 'text-blue-100',
      tabs: 'bg-blue-800',
      tabsText: 'text-blue-100',
      tabsIndicator: 'bg-blue-600',
    },
    [ThemePresets.R6_ORANGE]: {
      primary: '#ea580c',
      secondary: '#c2410c',
      accent: '#fb923c',
      background: '#1c1917',
      surface: '#292524',
      text: '#fef3c7',
      textSecondary: '#fde68a',
      border: '#44403c',
      hover: '#f97316',
      navbar: 'bg-orange-900',
      navbarText: 'text-orange-100',
      tabs: 'bg-orange-800',
      tabsText: 'text-orange-100',
      tabsIndicator: 'bg-orange-600',
    },
  };

  return themes[activeTheme] || themes[ThemePresets.DARK];
};

/**
 * Get Material Tailwind theme configuration
 * @param {string} themeName - Theme preset name
 * @returns {Object} Material Tailwind theme config
 */
export const getMaterialTailwindTheme = (themeName = ThemePresets.DARK) => {
  const colors = getThemeColors(themeName);

  return {
    tabsHeader: {
      defaultProps: {
        className: "",
      },
      styles: {
        base: {
          bg: colors.tabs,
        },
      },
    },
    tab: {
      defaultProps: {
        className: "",
        activeClassName: "",
        disabled: false,
      },
      styles: {
        base: {
          tab: {
            initial: {
              color: colors.tabsText,
            },
          },
          indicator: {
            bg: colors.tabsIndicator,
          },
        },
      },
    },
    popover: {
      styles: {
        base: {
          bg: colors.surface,
          border: `border border-${colors.border}`,
        }
      }
    },
    select: {
      styles: {
        base: {
          select: {
            color: colors.textSecondary,
            bg: colors.surface,
            backgroundColor: colors.surface,
          },
          option: {
            initial: {
              bg: colors.surface,
              backgroundColor: colors.surface,
              color: colors.textSecondary,
              background: colors.surface,
            },
            active: {
              bg: colors.hover,
              backgroundColor: colors.hover,
              color: colors.text,
            },
            disabled: {
              opacity: "opacity-50",
              cursor: "cursor-not-allowed",
              userSelect: "select-none",
              pointerEvents: "pointer-events-none",
            },
          },
          menu: {
            border: `1px solid ${colors.border}`,
            bg: colors.surface,
            backgroundColor: colors.surface,
            background: colors.surface,
            color: colors.textSecondary,
            opacity: "opacity-100",
          }
        }
      }
    },
    dialog: {
      styles: {
        base: {
          bg: colors.surface,
          backgroundColor: colors.surface,
        }
      }
    },
    menu: {
      styles: {
        base: {
          bg: colors.surface,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }
      }
    }
  };
};

/**
 * Helper function to determine if a theme is dark
 * @param {string} theme - Theme preset name
 * @returns {boolean} true if theme is dark
 */
export const isDarkTheme = (theme) => {
  return theme === ThemePresets.DARK || 
         theme === ThemePresets.CS_BLUE || 
         theme === ThemePresets.R6_ORANGE;
};

/**
 * Set theme preference
 * @param {string} themeName - Theme preset name
 */
export const setTheme = (themeName) => {
  if (typeof window !== 'undefined') {
    if (themeName !== ThemePresets.LIGHT && themeName !== ThemePresets.DARK) return;
    localStorage.setItem('theme', themeName);
    // Apply theme to document
    const themeToApply = themeName;
    
    document.documentElement.setAttribute('data-theme', themeToApply);
    document.documentElement.setAttribute('data-theme-mode', themeName);
    
    // Add or remove 'dark' class for Tailwind dark mode
    if (isDarkTheme(themeToApply)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }
};

/**
 * Get current theme preference
 * @returns {string} Current theme name
 */
export const getCurrentTheme = () => {
  const theme = getLocalStorage('theme', ThemePresets.DARK);
  return theme === ThemePresets.LIGHT || theme === ThemePresets.DARK ? theme : ThemePresets.DARK;
};

// Initialize theme on load
if (typeof window !== 'undefined') {
  const currentTheme = getCurrentTheme();
  let themeToApply = currentTheme;
  
  document.documentElement.setAttribute('data-theme', themeToApply);
  document.documentElement.setAttribute('data-theme-mode', currentTheme);
  
  // Add or remove 'dark' class for Tailwind dark mode
  if (isDarkTheme(themeToApply)) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  
}
